import {
  AccountingTransferForm,
  AccountingTransferLookups,
  AmendmentForm,
  AmendmentLookups,
  CloseMonthForm,
  DeactivateProviderFormFormik,
  ExistingProviderLookups,
  ModifyProviderFormFormik,
  ModifyProviderLookups,
  NewProviderLookups,
  ProviderFormFormik
} from '@common/types';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import { USER_ROLES } from '@core/redux/user/types';
import { getAmendmentEffectiveDateBounds } from '@features/production/amendment/utils';
import { IAbstractRecord } from '@models';
import { isEmpty, REGEX } from '@utils/validationUtils';

import { FormikProps } from 'formik';

export const personDetailsFormInitialValues = {
  mainBusinessPartner: { personId: '' },
  introducedBy: { id: '' },
  userId: '',
  personId: '',
  type: '',
  companyName: '',
  title: '',
  titleId: '',
  firstName: '',
  middleName: '',
  lastName: '',
  maidenName: '',
  initials: '',
  dateOfBirth: '',
  nationalityCode: '',
  email: '',
  phoneType: '',
  phoneNumber: '',
  relativeOfCode: '',
  smoker: 'NO',
  weight: 0,
  height: 0,
  dateOfDeath: '',
  dateOfDeathNotified: '',
  taxable: false,
  financialReference: '',
  occupationClass: '',
  occupationClassCode: '',
  profession: '',
  employer: '',
  dateOfEmployment: '',
  salary: 0,
  dateOfSelfCertificateReceived: '',
  dateOfSelfCertificateValidated: '',
  category: '',
  dateOfKYC: '',
  comments: '',
  managerName: '',
  relation: '',
  taxResidenceCountry: '',
  remarks: '',
  registrationPlace: '',
  registrationDate: '',
  legalRepresentativeFullName: '',
  legalRepresentativeDateOfBirth: '',
  legalRepresentativeNationality: ''
};

export const businessPartnerFormInitialValues = {
  newPerson: false,
  type: '',
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  companyName: '',
  person: '',
  email: '',
  mobileNumber: '',
  relatedCompany: '',
  bpType: '',
  isCompany: false,
  isLocal: false,
  isRegistered: false,
  registerNumber: '',
  registrationExpiryDate: '',
  addressType: '',
  country: '',
  zone: '',
  city: '',
  street: '',
  building: '',
  addressDetails: '',
  addressDetailsArabic: '',
  taxAuxiliary: '',
  auxiliary: '',
  maxCreditLimitAllowedCurrency: '',
  maxCreditLimitAllowed: '',
  allowAccessToAllInsured: false,
  overrideBroker: ''
};

export const businessPartnerDrawerSections = (
  defaultValues: IAbstractRecord,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  const isNewPerson = defaultValues?.newPerson === true;
  const isIndividual = defaultValues?.type === 'INDIVIDUAL';
  const isCompany = defaultValues?.type === 'COMPANY';

  return [
    {
      sectionTitle: 'Person Details',
      inputs: {
        newPerson: {
          name: 'newPerson',
          title: 'New Person',
          type: FormFieldType.switch,
          value: defaultValues.newPerson,
          required: true
        },
        type: {
          name: 'type',
          title: 'Type',
          placeholder: 'Person Type',
          type: FormFieldType.select,
          selectOptions: lovs.personTypes,
          value: defaultValues.type,
          required: isNewPerson,
          hidden: !isNewPerson
        },
        title: {
          name: 'title',
          title: 'Title',
          placeholder: 'Title',
          type: FormFieldType.select,
          selectOptions: lovs.personTitles,
          value: defaultValues.title,
          hidden: !(isNewPerson && isIndividual),
          required: isNewPerson && isIndividual
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          placeholder: 'First Name',
          type: FormFieldType.text,
          value: defaultValues.firstName,
          hidden: !(isNewPerson && isIndividual),
          required: isNewPerson && isIndividual
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          placeholder: 'Middle Name',
          type: FormFieldType.text,
          value: defaultValues.middleName,
          hidden: !(isNewPerson && isIndividual),
          required: isNewPerson && isIndividual
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          placeholder: 'Last Name',
          type: FormFieldType.text,
          value: defaultValues.lastName,
          hidden: !(isNewPerson && isIndividual),
          required: isNewPerson && isIndividual
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date of Birth',
          placeholder: 'Date of Birth',
          type: FormFieldType.date,
          value: defaultValues.dateOfBirth,
          hidden: !(isNewPerson && isIndividual),
          required: isNewPerson && isIndividual,
          maxDate: new Date()
        },
        companyName: {
          name: 'companyName',
          title: 'Company Name',
          placeholder: 'Company Name',
          type: FormFieldType.text,
          value: defaultValues.companyName,
          hidden: !(isNewPerson && isCompany),
          required: isNewPerson && isCompany
        },
        person: {
          name: 'person',
          title: 'Person',
          placeholder: 'Person',
          type: FormFieldType.select,
          selectOptions: lovs.activePersons,
          value: defaultValues.person,
          hidden: isNewPerson,
          required: !isNewPerson
        },
        email: {
          name: 'email',
          title: 'Email',
          placeholder: 'Email',
          type: FormFieldType.text,
          value: defaultValues.email,
          disabled: !defaultValues.newPerson,
          required: true,
          customValidator: ({ email }) => {
            return email && !REGEX.email.test(email) ? 'Invalid Email' : '';
          }
        },
        mobileNumber: {
          name: 'mobileNumber',
          title: 'Mobile Number',
          type: FormFieldType.phoneNumber,
          value: defaultValues.mobileNumber,
          disabled: !defaultValues.newPerson,
          required: true
        }
      }
    },
    {
      sectionTitle: 'Business Partner Details',
      inputs: {
        relatedCompany: {
          name: 'relatedCompany',
          title: 'Related Company',
          placeholder: 'Related Company',
          type: FormFieldType.select,
          selectOptions: lovs.relatedCompanies,
          value: defaultValues.relatedCompany,
          required: true
        },
        bpType: {
          name: 'bpType',
          title: 'Type',
          placeholder: 'Type',
          type: FormFieldType.select,
          selectOptions: lovs.businessPartnerTypes,
          value: defaultValues.bpType,
          required: true
        },
        isCompany: {
          name: 'isCompany',
          title: 'Is Company',
          type: FormFieldType.switch,
          value: defaultValues.isCompany,
          required: true
        },
        isLocal: {
          name: 'isLocal',
          title: 'Is Local',
          type: FormFieldType.switch,
          value: defaultValues.isLocal,
          required: true
        },
        isRegistered: {
          name: 'isRegistered',
          title: 'Is Registered',
          type: FormFieldType.switch,
          value: defaultValues.isRegistered,
          required: true
        },
        registerNumber: {
          name: 'registerNumber',
          title: 'Registration Number',
          placeholder: 'Registration Number',
          type: FormFieldType.text,
          value: defaultValues.registerNumber
        },
        registrationExpiryDate: {
          name: 'registrationExpiryDate',
          title: 'Registration Expiry Date',
          type: FormFieldType.date,
          value: defaultValues.registrationExpiryDate
        }
      }
    },
    {
      sectionTitle: 'Primary Address Details',
      hidden: !isNewPerson,
      inputs: {
        addressType: {
          name: 'addressType',
          title: 'Address Type',
          placeholder: 'Select Address Type',
          type: FormFieldType.select,
          selectOptions: lovs.addressTypes,
          value: defaultValues.addressType,
          required: isNewPerson
        },
        country: {
          name: 'country',
          title: 'Country',
          placeholder: 'Country',
          type: FormFieldType.select,
          selectOptions: lovs.countries,
          value: defaultValues.country,
          required: isNewPerson
        },
        zone: {
          name: 'zone',
          title: 'Zone',
          placeholder: 'Zone',
          type: FormFieldType.select,
          selectOptions:
            defaultValues.country === 'LB' && lovs['zones']
              ? lovs['zones']
              : {},
          value: defaultValues.zone
        },
        city: {
          name: 'city',
          title: 'City',
          placeholder: 'City',
          type: FormFieldType.text,
          value: defaultValues.city,
          required: isNewPerson
        },
        street: {
          name: 'street',
          title: 'Street',
          placeholder: 'Street',
          type: FormFieldType.text,
          value: defaultValues.street
        },
        building: {
          name: 'building',
          title: 'Building',
          placeholder: 'Building',
          type: FormFieldType.text,
          value: defaultValues.building
        },
        addressDetails: {
          name: 'addressDetails',
          title: 'Address Details',
          placeholder: 'Address Details',
          type: FormFieldType.text,
          value: defaultValues.addressDetails
        },
        addressDetailsArabic: {
          name: 'addressDetailsArabic',
          title: 'Address Details (Arabic)',
          placeholder: 'Address Details In Arabic',
          type: FormFieldType.text,
          value: defaultValues.addressDetailsArabic
        }
      }
    },
    {
      sectionTitle: 'Accounting Details',
      inputs: {
        taxAuxiliary: {
          name: 'taxAuxiliary',
          title: 'Tax Auxiliary',
          placeholder: 'Tax Auxiliary',
          type: FormFieldType.text,
          value: defaultValues.taxAuxiliary,
          required: true,
          customValidator: ({ taxAuxiliary }) => {
            return taxAuxiliary.length != 8
              ? 'Tax Auxiliary should be 8 characters'
              : '';
          }
        },
        auxiliary: {
          name: 'auxiliary',
          title: 'Auxiliary',
          placeholder: 'Auxiliary',
          type: FormFieldType.text,
          value: defaultValues.auxiliary,
          required: true,
          customValidator: ({ auxiliary }) => {
            return auxiliary.length != 8
              ? 'Auxiliary should be 8 characters'
              : '';
          }
        }
      }
    },
    {
      sectionTitle: 'Credit Limit Details',
      inputs: {
        maxCreditLimitAllowedCurrency: {
          name: 'maxCreditLimitAllowedCurrency',
          title: 'Max Credit Limit Allowed (Currency)',
          placeholder: 'Currency',
          type: FormFieldType.select,
          selectOptions: lovs.currencies,
          value: defaultValues.maxCreditLimitAllowedCurrency,
          required: true
        },
        maxCreditLimitAllowed: {
          name: 'maxCreditLimitAllowed',
          title: 'Max Credit Limit Allowed',
          placeholder: 'Max Credit Limit Allowed',
          type: FormFieldType.currency,
          maxDecimalPrecision: 0,
          value: defaultValues.maxCreditLimitAllowed,
          required: true
        }
      }
    },
    {
      sectionTitle: 'Additional Details',
      inputs: {
        allowAccessToAllInsured: {
          name: 'allowAccessToAllInsured',
          title: 'Allow Access to All Insureds',
          type: FormFieldType.switch,
          value: defaultValues.allowAccessToAllInsured,
          required: true
        },
        overrideBroker: {
          name: 'overrideBroker',
          title: 'Override Broker',
          placeholder: 'Override Broker',
          type: FormFieldType.autocomplete,
          initialOptions: lovs.activeBusinessPartners,
          value: defaultValues.overrideBroker,
          required: false,
          canClearValue: true
        }
      }
    }
  ];
};

export const providerDrawerSections = (
  providerFormik: FormikProps<ProviderFormFormik>,
  lovs: NewProviderLookups | ExistingProviderLookups
): IDynamicFormWidgetSection[] => {
  const isNewPerson = providerFormik?.values?.newPerson === true;

  return [
    {
      sectionTitle: 'Person Details',
      inputs: {
        newPerson: {
          name: 'newPerson',
          title: 'New Person',
          type: FormFieldType.switch,
          value: providerFormik?.values?.newPerson,
          required: true
        },
        title: {
          name: 'title',
          title: 'Title',
          placeholder: 'Title',
          type: FormFieldType.select,
          selectOptions: (lovs as NewProviderLookups)?.titles,
          value: providerFormik?.values?.title,
          hidden: !isNewPerson,
          required: isNewPerson
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          placeholder: 'First Name',
          type: FormFieldType.text,
          value: providerFormik?.values?.firstName,
          hidden: !isNewPerson,
          required: isNewPerson
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          placeholder: 'Middle Name',
          type: FormFieldType.text,
          value: providerFormik?.values?.middleName,
          hidden: !isNewPerson,
          required: isNewPerson
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          placeholder: 'Last Name',
          type: FormFieldType.text,
          value: providerFormik?.values?.lastName,
          hidden: !isNewPerson,
          required: isNewPerson
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date of Birth',
          placeholder: 'Date of Birth',
          type: FormFieldType.date,
          value: providerFormik?.values?.dateOfBirth,
          hidden: !isNewPerson,
          required: isNewPerson,
          maxDate: new Date()
        },
        person: {
          name: 'personId',
          title: 'Person',
          placeholder: 'Person',
          type: FormFieldType.select,
          selectOptions: (lovs as ExistingProviderLookups)?.persons,
          value: providerFormik?.values?.personId,
          hidden: isNewPerson,
          required: !isNewPerson
        },
        email: {
          name: 'email',
          title: 'Email',
          placeholder: 'Email',
          type: FormFieldType.text,
          value: providerFormik?.values?.email,
          required: isNewPerson,
          disabled: !isNewPerson,
          customValidator: ({ email }) => {
            return email && !REGEX.email.test(email) ? 'Invalid Email' : '';
          }
        },
        phoneType: {
          name: 'phoneType',
          title: 'Phone Type',
          type: FormFieldType.select,
          placeholder: 'Phone Type',
          selectOptions: (lovs as NewProviderLookups)?.phoneType,
          value: providerFormik?.values?.phoneType,
          required: isNewPerson,
          disabled: !isNewPerson
        },
        phoneNumber: {
          name: 'phoneNumber',
          title: 'Phone Number',
          type: FormFieldType.phoneNumber,
          placeholder: 'Phone Number',
          value: providerFormik?.values?.phoneNumber,
          required: isNewPerson,
          disabled: !isNewPerson
        },
        isTaxable: {
          name: 'isTaxable',
          title: 'Taxable',
          type: FormFieldType.switch,
          value: providerFormik?.values?.isTaxable,
          required: isNewPerson,
          disabled: !isNewPerson
        }
      }
    },
    {
      sectionTitle: 'Primary Address Details',
      hidden: !isNewPerson,
      inputs: {
        addressType: {
          name: 'addressType',
          title: 'Address Type',
          placeholder: 'Select Address Type',
          type: FormFieldType.select,
          selectOptions: (lovs as NewProviderLookups)?.addressType,
          value: providerFormik?.values?.addressType,
          required: isNewPerson
        },
        country: {
          name: 'address.country',
          title: 'Country',
          placeholder: 'Country',
          type: FormFieldType.select,
          selectOptions: (lovs as NewProviderLookups)?.countries,
          value: providerFormik?.values?.address?.country,
          required: isNewPerson
        },
        zone: {
          name: 'address.zone',
          title: 'Zone',
          placeholder: 'Zone',
          type: FormFieldType.select,
          selectOptions:
            providerFormik?.values?.address?.country === 'LB' &&
            (lovs as NewProviderLookups)?.zones
              ? (lovs as NewProviderLookups)?.zones
              : {},
          value: providerFormik?.values?.address?.zone,
          hidden: providerFormik?.values?.address?.country !== 'LB'
        },
        city: {
          name: 'address.city',
          title: 'City',
          placeholder: 'City',
          type: FormFieldType.text,
          value: providerFormik?.values?.address?.city,
          required: isNewPerson
        },
        street: {
          name: 'address.street',
          title: 'Street',
          placeholder: 'Street',
          type: FormFieldType.text,
          value: providerFormik?.values?.address?.street
        },
        building: {
          name: 'address.building',
          title: 'Building',
          placeholder: 'Building',
          type: FormFieldType.text,
          value: providerFormik?.values?.address?.building
        },
        addressDetails: {
          name: 'address.addressDetails',
          title: 'Address Details',
          placeholder: 'Address Details',
          type: FormFieldType.text,
          value: providerFormik?.values?.address?.addressDetails
        },
        addressDetailsArabic: {
          name: 'address.addressDetailsArabic',
          title: 'Address Details (Arabic)',
          placeholder: 'Address Details In Arabic',
          type: FormFieldType.text,
          value: providerFormik?.values?.address?.addressDetailsArabic
        }
      }
    },
    {
      sectionTitle: 'Provider Details',
      inputs: {
        type: {
          name: 'type',
          title: 'Type',
          placeholder: 'Type',
          type: FormFieldType.select,
          selectOptions: lovs?.providerTypes,
          value: providerFormik?.values?.type,
          required: true
        },
        isCompany: {
          name: 'isCompany',
          title: 'Company',
          type: FormFieldType.switch,
          value: providerFormik?.values?.isCompany,
          required: true
        },
        isLocal: {
          name: 'isLocal',
          title: 'Local',
          type: FormFieldType.switch,
          value: providerFormik?.values?.isLocal,
          required: true
        },
        contractStartDate: {
          name: 'contractStartDate',
          title: 'Contract Start Date',
          type: FormFieldType.date,
          value: providerFormik?.values?.contractStartDate,
          maxDate: new Date(providerFormik?.values?.contractEndDate),
          required: true
        },
        contractEndDate: {
          name: 'contractEndDate',
          title: 'Contract End Date',
          type: FormFieldType.date,
          value: providerFormik?.values?.contractEndDate,
          minDate: new Date(providerFormik?.values?.contractStartDate),
          hidden:
            !providerFormik?.values?.status ||
            providerFormik?.values?.status?.toUpperCase() === 'ACTIVE',
          required: providerFormik?.values?.status?.toUpperCase() !== 'ACTIVE'
        },
        status: {
          name: 'status',
          title: 'Status',
          type: FormFieldType.select,
          placeholder: 'Select Status',
          value: !isEmpty(providerFormik?.values?.status)
            ? providerFormik?.values?.status
            : [],
          required: true,
          selectOptions: lovs?.status
        }
      }
    },
    {
      sectionTitle: 'Auxiliary Details',
      inputs: {
        auxiliary: {
          name: 'auxiliary',
          title: 'Auxiliary',
          placeholder: 'Auxiliary',
          type: FormFieldType.text,
          value: providerFormik?.values?.auxiliary,
          required: true,
          customValidator: ({ auxiliary }) =>
            auxiliary?.length !== 8 ? 'Auxiliary should be 8 characters' : ''
        },
        taxAuxiliary: {
          name: 'taxAuxiliary',
          title: 'Tax Auxiliary',
          placeholder: 'Tax Auxiliary',
          type: FormFieldType.text,
          value: providerFormik?.values?.taxAuxiliary,
          required: true,
          customValidator: ({ taxAuxiliary }) =>
            taxAuxiliary?.length !== 8
              ? 'Tax Auxiliary should be 8 characters'
              : ''
        }
      }

    }
  ];
};

export const modifyProviderDrawerSections = (
  providerFormik: FormikProps<ModifyProviderFormFormik>,
  lovs: ModifyProviderLookups
): IDynamicFormWidgetSection[] => {
  return [
    {
      sectionTitle: 'Person Details',
      inputs: {
        title: {
          name: 'title',
          title: 'Title',
          placeholder: 'Title',
          type: FormFieldType.select,
          selectOptions: (lovs as NewProviderLookups)?.titles,
          value: providerFormik?.values?.title,
          disabled: true,
          required: true
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          placeholder: 'First Name',
          type: FormFieldType.text,
          value: providerFormik?.values?.firstName,
          disabled: true,
          required: true
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          placeholder: 'Middle Name',
          type: FormFieldType.text,
          value: providerFormik?.values?.middleName,
          disabled: true,
          required: true
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          placeholder: 'Last Name',
          type: FormFieldType.text,
          value: providerFormik?.values?.lastName,
          disabled: true,
          required: true
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date of Birth',
          placeholder: 'Date of Birth',
          type: FormFieldType.date,
          value: providerFormik?.values?.dateOfBirth,
          disabled: true,
          required: true
        },
        email: {
          name: 'email',
          title: 'Email',
          placeholder: 'Email',
          type: FormFieldType.text,
          value: providerFormik?.values?.email,
          disabled: true,
          required: false,
          customValidator: ({ email }) => {
            return email && !REGEX.email.test(email) ? 'Invalid Email' : '';
          }
        },
        phoneType: {
          name: 'phoneType',
          title: 'Phone Type',
          type: FormFieldType.select,
          placeholder: 'Phone Type',
          selectOptions: (lovs as NewProviderLookups)?.phoneType,
          value: providerFormik?.values?.phoneType,
          disabled: true,
          required: true
        },
        phoneNumber: {
          name: 'phoneNumber',
          title: 'Phone Number',
          type: FormFieldType.phoneNumber,
          placeholder: 'Phone Number',
          value: providerFormik?.values?.phoneNumber,
          disabled: true,
          required: false
        },
        isTaxable: {
          name: 'isTaxable',
          title: 'Taxable',
          type: FormFieldType.switch,
          value: providerFormik?.values?.isTaxable,
          disabled: true
        }
      }
    },
    {
      sectionTitle: 'Provider Details',
      inputs: {
        type: {
          name: 'type',
          title: 'Type',
          placeholder: 'Type',
          type: FormFieldType.select,
          selectOptions: lovs?.providerTypes,
          value: providerFormik?.values?.type,
          disabled: true,
          required: true
        },
        isCompany: {
          name: 'isCompany',
          title: 'Company',
          type: FormFieldType.switch,
          value: providerFormik?.values?.isCompany
        },
        isLocal: {
          name: 'isLocal',
          title: 'Local',
          type: FormFieldType.switch,
          value: providerFormik?.values?.isLocal
        },
        contractStartDate: {
          name: 'contractStartDate',
          title: 'Contract Start Date',
          type: FormFieldType.date,
          value: providerFormik?.values?.contractStartDate,
          maxDate: providerFormik?.values?.contractEndDate
            ? new Date(providerFormik.values.contractEndDate)
            : undefined,
          required: true
        }
      }
    },
    {
      sectionTitle: 'Auxiliary Details',
      inputs: {
        auxiliary: {
          name: 'auxiliary',
          title: 'Auxiliary',
          placeholder: 'Auxiliary',
          type: FormFieldType.text,
          value: providerFormik?.values?.auxiliary,
          required: true,
          customValidator: ({ auxiliary }) =>
            auxiliary?.length !== 8 ? 'Auxiliary should be 8 characters' : ''
        },
        taxAuxiliary: {
          name: 'taxAuxiliary',
          title: 'Tax Auxiliary',
          placeholder: 'Tax Auxiliary',
          type: FormFieldType.text,
          value: providerFormik?.values?.taxAuxiliary,
          required: true,
          customValidator: ({ taxAuxiliary }) =>
            taxAuxiliary?.length !== 8
              ? 'Tax Auxiliary should be 8 characters'
              : ''
        }
      }

    }
  ];
};

export const deactivateProviderDrawerSections = (
  providerFormik: FormikProps<DeactivateProviderFormFormik>
): IDynamicFormWidgetSection[] => {
  return [
    {
      sectionTitle: '',
      inputs: {
        contractEndDate: {
          name: 'contractEndDate',
          title: 'Contract End Date',
          type: FormFieldType.date,
          value: providerFormik?.values?.contractEndDate,
          minDate: new Date(providerFormik?.values?.contractStartDate),
          maxDate: new Date(),
          required: true
        }
      }
    }
  ];
};

export const initialValues = {
  rangeCode: '',
  rangeYear: '',
  rangeFrom: 0,
  rangeTo: 0,
  rangeStatus: 'ACTIVE'
};

export const vignetteRangeDrawerSections: IDynamicFormWidgetSection[] = [
  {
    inputs: {
      rangeYear: {
        name: 'rangeYear',
        title: 'Year',
        type: FormFieldType.number,
        placeholder: 'Enter Year',
        value: initialValues.rangeYear,
        required: true,
        customValidator: (values) => {
          const year = parseInt(values.rangeYear);
          if (isNaN(year) || year < 1900 || year > 2100) {
            return 'Invalid Year format';
          }

          return '';
        }
      },
      rangeCode: {
        name: 'rangeCode',
        title: 'Code',
        type: FormFieldType.text,
        placeholder: 'Enter Vignette Code',
        value: initialValues.rangeCode,
        required: true
      },
      rangeFrom: {
        name: 'rangeFrom',
        title: 'Range From',
        type: FormFieldType.number,
        placeholder: 'Enter Range From',
        value: initialValues.rangeFrom || '',
        required: true,
        maxDecimalPrecision: 0
      },
      rangeTo: {
        name: 'rangeTo',
        title: 'Range To',
        type: FormFieldType.number,
        placeholder: 'Enter Range To',
        value: initialValues.rangeTo || '',
        required: true,
        maxDecimalPrecision: 0,
        customValidator: (allValues) => {
          const min = parseFloat(allValues.rangeFrom ?? 0);
          const max = parseFloat(allValues.rangeTo ?? 0);
          if (min && max && min > max)
            return 'Range To cannot be less than Range From';

          return '';
        }
      },
      rangeStatus: {
        name: 'rangeStatus',
        title: 'Status',
        type: FormFieldType.select,
        placeholder: 'Select Status',
        value: !isEmpty(initialValues.rangeStatus)
          ? initialValues.rangeStatus
          : [],
        required: true,
        hidden: true,
        selectOptions: {}
      }
    }
  }
];

export const getModifyDrawerSections = (
  defaultValues: IAbstractRecord,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  const isIndividual = defaultValues?.type === 'INDIVIDUAL';
  const isCompany = defaultValues?.type === 'COMPANY';
  const isNewStatus = defaultValues?.status?.toLowerCase() === 'new';

  return [
    {
      sectionTitle: 'Person Details',
      inputs: {
        title: {
          name: 'title',
          title: 'Title',
          type: FormFieldType.select,
          selectOptions: lovs.personTitles,
          value: defaultValues?.title,
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          type: FormFieldType.text,
          value: defaultValues?.firstName,
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          type: FormFieldType.text,
          value: defaultValues?.middleName,
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          type: FormFieldType.text,
          value: defaultValues?.lastName,
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date Of Birth',
          type: FormFieldType.date,
          value: defaultValues?.dateOfBirth,
          maxDate: new Date(),
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        companyName: {
          name: 'companyName',
          title: 'Company Name',
          type: FormFieldType.text,
          value: defaultValues?.companyName,
          hidden: !isCompany,
          required: isCompany,
          disabled: true
        },
        email: {
          name: 'email',
          title: 'Email',
          type: FormFieldType.text,
          value: defaultValues?.email,
          required: true,
          customValidator: ({ email }) =>
            email && !REGEX.email.test(email) ? 'Invalid Email' : '',
          disabled: true
        },
        mobileNumber: {
          name: 'mobileNumber',
          title: 'Mobile Number',
          type: FormFieldType.phoneNumber,
          value: defaultValues?.mobileNumber,
          required: true,
          disabled: true
        }
      }
    },
    {
      sectionTitle: 'Business Partner Details',
      inputs: {
        relatedCompany: {
          name: 'relatedCompany',
          title: 'Related Company',
          type: FormFieldType.select,
          selectOptions: lovs.relatedCompanies,
          value: defaultValues?.relatedCompany,
          disabled: !isNewStatus,
          conditionalRequired: (values) =>
            values?.status?.toLowerCase() === 'new'
        },
        bpType: {
          name: 'bpType',
          title: 'Type',
          type: FormFieldType.select,
          selectOptions: lovs.businessPartnerTypes,
          value: defaultValues?.bpType,
          required: true
        },
        isCompany: {
          name: 'isCompany',
          title: 'Is Company',
          type: FormFieldType.switch,
          value: defaultValues?.isCompany,
          required: true
        },
        isLocal: {
          name: 'isLocal',
          title: 'Is Local',
          type: FormFieldType.switch,
          value: defaultValues?.isLocal,
          required: true
        },
        isRegistered: {
          name: 'isRegistered',
          title: 'Is Registered',
          type: FormFieldType.switch,
          value: defaultValues?.isRegistered,
          required: true
        },
        registerNumber: {
          name: 'registerNumber',
          title: 'Registration Number',
          placeholder: 'Registration Number',
          type: FormFieldType.text,
          value: defaultValues?.registerNumber
        },
        registrationExpiryDate: {
          name: 'registrationExpiryDate',
          title: 'Registration Expiry Date',
          type: FormFieldType.date,
          value: defaultValues?.registrationExpiryDate
        }
      }
    },
    {
      sectionTitle: 'Accounting Details',
      inputs: {
        taxAuxiliary: {
          name: 'taxAuxiliary',
          title: 'Tax Auxiliary',
          type: FormFieldType.text,
          value: defaultValues?.taxAuxiliary,
          required: true,
          customValidator: ({ taxAuxiliary }) =>
            taxAuxiliary?.length !== 8
              ? 'Tax Auxiliary should have 8 characters'
              : ''
        },
        auxiliary: {
          name: 'auxiliary',
          title: 'Auxiliary',
          type: FormFieldType.text,
          value: defaultValues?.auxiliary,
          required: true,
          customValidator: ({ auxiliary }) =>
            auxiliary?.length !== 8 ? 'Auxiliary should have 8 characters' : ''
        }
      }
    },
    {
      sectionTitle: 'Additional Details',
      inputs: {
        allowAccessToAllInsured: {
          name: 'allowAccessToAllInsured',
          title: 'Allow Access to All Insureds',
          type: FormFieldType.switch,
          value: defaultValues?.allowAccessToAllInsured,
          required: true
        },
        overrideBroker: {
          name: 'overrideBroker',
          title: 'Override Broker',
          placeholder: 'Override Broker',
          type: FormFieldType.autocomplete,
          initialOptions: lovs.activeBusinessPartners,
          value: defaultValues?.overrideBroker,
          required: false,
          canClearValue: true
        }
      }
    }
  ];
};

export const accountingTransferDrawerSections = (
  formik: FormikProps<AccountingTransferForm>,
  lovs: AccountingTransferLookups
): IDynamicFormWidgetSection[] => {
  const from = formik.values.transferFrom
    ? new Date(formik.values.transferFrom as any)
    : undefined;
  // const to = formik.values.transferTo
  //   ? new Date(formik.values.transferTo as any)
  //   : undefined;

  return [
    {
      inputs: {
        transferTypeId: {
          name: 'transferType',
          title: 'Transfer Type',
          placeholder: 'Select Transfer Type',
          type: FormFieldType.select,
          selectOptions: lovs.transferTypes,
          value: formik.values.transferType,
          required: true
        },
        transferFrom: {
          name: 'transferFrom',
          title: 'Transfer From',
          placeholder: 'Select Transfer Start Date',
          type: FormFieldType.date,
          value: formik.values.transferFrom,
          required: true
        },
        transferTo: {
          name: 'transferTo',
          title: 'Transfer To',
          placeholder: 'Select Transfer End Date',
          type: FormFieldType.date,
          value: formik.values.transferTo,
          required: true,
          minDate: from
        }
      }
    }
  ];
};

export const getUserDrawerSections = (
  businessUserId: string,
  defaultValues: Record<string, any> = initialValues,
  lookups: Record<string, Record<string, string>>,
  isNewPerson: boolean
): IDynamicFormWidgetSection[] => [
  {
    sectionTitle: 'Person Details',
    inputs: {
      newPerson: {
        name: 'newPerson',
        title: 'New Person',
        type: FormFieldType.switch,
        value: defaultValues.newPerson,
        required: true,
        disabled: false,
        hidden: !!businessUserId
      },
      title: {
        name: 'title',
        title: 'Title',
        type: FormFieldType.select,
        placeholder: 'Title',
        value: defaultValues?.title ?? defaultValues?.person?.title ?? '',
        selectOptions: lookups['Titles'],
        hidden: businessUserId ? false : !isNewPerson,
        required: isNewPerson && !businessUserId,
        disabled: !!businessUserId
      },
      firstName: {
        name: 'firstName',
        title: 'First Name',
        placeholder: 'First Name',
        type: FormFieldType.text,
        value: defaultValues?.user?.firstName,
        hidden: businessUserId ? false : !isNewPerson,
        required: businessUserId ? true : isNewPerson,
        disabled: !!businessUserId
      },
      middleName: {
        name: 'middleName',
        title: 'Middle Name',
        placeholder: 'Middle Name',
        type: FormFieldType.text,
        value: defaultValues?.user?.middleName,
        hidden: businessUserId ? false : !isNewPerson,
        required: businessUserId ? true : isNewPerson,
        disabled: !!businessUserId
      },
      lastName: {
        name: 'lastName',
        title: 'Last Name',
        placeholder: 'Last Name',
        type: FormFieldType.text,
        value: defaultValues?.user?.lastName,
        hidden: businessUserId ? false : !isNewPerson,
        required: businessUserId ? true : isNewPerson,
        disabled: !!businessUserId
      },
      dateOfBirth: {
        name: 'dateOfBirth',
        title: 'Date of Birth',
        placeholder: 'Date of Birth',
        type: FormFieldType.date,
        value: defaultValues.dateOfBirth,
        hidden: !isNewPerson,
        required: isNewPerson,
        disabled: false,
        maxDate: new Date()
      },
      person: {
        name: 'person',
        title: 'Person',
        placeholder: 'Select Person',
        type: FormFieldType.select,
        value: defaultValues.person,
        hidden: isNewPerson || !!businessUserId,
        required: !isNewPerson && !businessUserId,
        disabled: false,
        selectOptions: lookups['ActivePersons']
      },
      email: {
        name: 'email',
        title: 'Email',
        placeholder: 'Email',
        type: FormFieldType.text,
        value: defaultValues?.user?.email,
        required: true,
        disabled: !!businessUserId || !isNewPerson,
        customValidator(values) {
          const email = values?.email;
          const regex = REGEX.email;
          if (email && !regex.test(email)) {
            return 'Invalid Email';
          }

          return '';
        }
      },
      password: {
        name: 'password',
        title: 'Password',
        placeholder: 'Password',
        type: FormFieldType.password,
        value: defaultValues?.user?.password,
        strongValidation: true,
        hidden: !!businessUserId,
        required: false,
        disabled: false
      },
      mobileNumber: {
        name: 'mobileNumber',
        title: 'Mobile Number',
        placeholder: 'Mobile Number',
        type: FormFieldType.phoneNumber,
        value: defaultValues?.user?.phoneNumber || '',
        required: true,
        disabled: !!businessUserId || !isNewPerson
      }
    }
  },
  {
    sectionTitle: 'Access Details',
    inputs: {
      access: {
        name: 'access',
        title: 'Access Type',
        type: FormFieldType.select,
        placeholder: 'Access Type',
        value: defaultValues.user?.access,
        selectOptions: lookups['access'],
        required: true,
        disabled: false
      },
      role: {
        name: 'role',
        title: 'Role',
        type: FormFieldType.select,
        placeholder: 'Role',
        value:
          defaultValues?.user?.userRoles
            ?.map((userRole: any) => userRole.role?.name)
            .join(', ') || '',
        selectOptions:
          defaultValues?.user?.access === 'POS'
            ? Object.fromEntries(
                Object.entries(lookups['Roles']).filter(([key]) =>
                  (
                    [
                      USER_ROLES['POS-Quoting'],
                      USER_ROLES['POS-Issuance']
                    ] as string[]
                  ).includes(key)
                )
              )
            : defaultValues?.user?.access === 'Backoffice'
              ? Object.fromEntries(
                  Object.entries(lookups['Roles']).filter(([key]) =>
                    ['Admin', 'Underwriter', 'Accountant', 'Claim Officer','Reinsurance Agent','Commercial'].includes(key)
                  )
                )
              : lookups['Roles'],
        required: true,
        disabled: false
      },
      type: {
        name: 'type',
        title: 'Type',
        type: FormFieldType.select,
        placeholder: 'Type',
        value: defaultValues?.user?.type,
        selectOptions: lookups['UserType'],
        required: true,
        disabled: false
      }
    }
  },

  {
    sectionTitle: 'Primary Address Details',
    hidden: !!businessUserId || !isNewPerson,
    inputs: {
      addressType: {
        name: 'addressType',
        title: 'Address Type',
        placeholder: 'Address Type',
        type: FormFieldType.select,
        value: defaultValues.addressType,
        selectOptions: lookups['PersonAddressType'],
        required: !businessUserId && isNewPerson,
        disabled: false
      },
      country: {
        name: 'country',
        title: 'Country',
        type: FormFieldType.select,
        placeholder: 'Country',
        value: defaultValues.country,
        selectOptions: lookups['countries'],
        required: !businessUserId && isNewPerson,
        disabled: false
      },
      zone: {
        name: 'zone',
        title: 'Zone',
        type: FormFieldType.select,
        placeholder: 'Zone',
        value: defaultValues.zone,
        selectOptions:
          defaultValues.country === 'LB' && lookups['zones']
            ? lookups['zones']
            : {},
        required: false,
        disabled: false
      },

      city: {
        name: 'city',
        title: 'City',
        placeholder: 'City',
        type: FormFieldType.text,
        value: defaultValues.city,
        required: !businessUserId && isNewPerson,
        disabled: false
      },
      street: {
        name: 'street',
        title: 'Street',
        placeholder: 'Street',
        type: FormFieldType.text,
        value: defaultValues.street,
        required: false,
        disabled: false
      },
      building: {
        name: 'building',
        title: 'Building',
        placeholder: 'Building',
        type: FormFieldType.text,
        value: defaultValues.street,
        required: false,
        disabled: false
      },
      addressDetails: {
        name: 'addressDetails',
        title: 'Address Details',
        placeholder: 'Address Details',
        type: FormFieldType.text,
        value: defaultValues.addressDetails,
        required: false,
        disabled: false
      },
      addressDetailsArabic: {
        name: 'addressDetailsArabic',
        title: 'Address Details Arabic',
        placeholder: 'Address Details in Arabic',
        type: FormFieldType.text,
        value: defaultValues.addressDetailsArabic,
        required: false,
        disabled: false
      }
    }
  },
  {
    sectionTitle: 'Additional Details',
    hidden: !!businessUserId,
    inputs: {
      status: {
        name: 'status',
        title: 'Status',
        type: FormFieldType.select,
        placeholder: 'Status',
        value: defaultValues.status || 'ACTIVE',
        selectOptions: lookups['UserStatus'],
        required: !businessUserId,
        disabled: false
      }
    }
  }
];

export const amendmentDrawerSections = (
  formik: FormikProps<AmendmentForm>,
  lovs: AmendmentLookups
): IDynamicFormWidgetSection[] => {
  const isEndorsement = formik.values.amendmentType === 'POLICY_ENDORSEMENT';
  const isCancellation = formik.values.amendmentType === 'POLICY_CANCELLATION';
  const effectiveDateBounds = isCancellation
    ? getAmendmentEffectiveDateBounds(
        lovs.policyEffectiveDate,
        lovs.policyExpiryDate
      )
    : null;

  return [
    {
      inputs: {
        amendmentType: {
          name: 'amendmentType',
          title: 'Amendment Type',
          placeholder: 'Select Amendment Type',
          type: FormFieldType.select,
          selectOptions: lovs.amendmentTypes,
          value: formik.values.amendmentType,
          required: true
        },

        amendmentSubtypeId: {
          name: 'amendmentSubtypeId',
          title: 'Amendment Sub Type',
          placeholder: 'Select Amendment Sub Type',
          type: FormFieldType.select,
          selectOptions: lovs.amendmentSubtypes,
          value: formik.values.amendmentSubtypeId ?? '',
          hidden: !isEndorsement,
          required: isEndorsement
        },

        amendmentIssueDate: {
          name: 'amendmentIssueDate',
          title: isEndorsement
            ? 'Amendment Issue Date'
            : 'Cancellation Issue Date',
          placeholder: isEndorsement
            ? 'Amendment Issue Date'
            : 'Cancellation Issue Date',
          type: FormFieldType.date,
          value: formik.values.amendmentIssueDate,
          required: true,
          disabled: !lovs.amendmentIssueDateEditable,
          minDate: lovs.amendmentIssueDateMinDate,
          maxDate: lovs.amendmentIssueDateMaxDate
        },

        amendmentEffectiveDate: {
          name: 'amendmentEffectiveDate',
          title: isEndorsement
            ? 'Amendment Effective Date'
            : 'Cancellation Effective Date',
          placeholder: isEndorsement
            ? 'Amendment Effective Date'
            : 'Cancellation Effective Date',
          type: FormFieldType.date,
          value: formik.values.amendmentEffectiveDate,
          required: true,
          disabled: !isCancellation,
          minDate: effectiveDateBounds?.minDate,
          maxDate: effectiveDateBounds?.maxDate
        },

        cancellationReason: {
          name: 'amendmentReason',
          title: 'Cancellation Reason',
          placeholder: 'Cancellation Reason',
          type: FormFieldType.text,
          value: formik.values.amendmentReason ?? '',
          hidden: !isCancellation,
          required: isCancellation,
          disabled: !isCancellation
        }
      }
    }
  ];
};

export const userFormInitialValues = (newPerson?: boolean) => ({
  person: '',
  email: '',
  password: '',
  type: '',
  account: '',
  access: '',
  SalesforceManagement_BusinessUserStatuses: 'ACTIVE',
  personId: '',
  newPerson: newPerson || false,
  isAdmin: false,
  role: '',
  title: '',
  firstName: '',
  lastName: '',
  middleName: '',
  mobileNumber: '',
  addressType: '',
  address: {
    country: '',
    city: '',
    street: '',
    zone: '',
    building: ''
  },
  addressDetails: '',
  addressDetailsArabic: '',
  status: 'ACTIVE',
  user: {
    email: '',
    password: '',
    access: '',
    type: '',
    phoneNumber: '',
    firstName: '',
    middleName: '',
    lastName: '',
    title: ''
  }
});

export const closeMonthFormInitialValues: CloseMonthForm = {
  closingTypeID: '',
  closingPeriodFrom: '',
  closingPeriodTo: ''
};

type CloseMonthUserRights = {
  reinsuranceClosingRights: boolean;
  productionClosingRights: boolean;
};

export const closeMonthDrawerSections = (
  formik: FormikProps<CloseMonthForm>,
  lovs: Record<string, Record<string, string>>,
  userRights: CloseMonthUserRights = {
    reinsuranceClosingRights: false,
    productionClosingRights: false
  }
): IDynamicFormWidgetSection[] => {
  const filteredClosingTypes = Object.fromEntries(
    Object.entries(lovs.monthClosingTypes || {}).filter(([code]) => {
      if (code === 'PRD') return userRights.productionClosingRights;
      if (code === 'RI') return userRights.reinsuranceClosingRights;
      return false;
    })
  );

  const dateRangeValidator = (values: IAbstractRecord) => {
    if (values.closingPeriodFrom && values.closingPeriodTo) {
      const fromDate = new Date(values.closingPeriodFrom);
      const toDate = new Date(values.closingPeriodTo);
      if (fromDate > toDate) {
        return 'Closing period from cannot be greater than closing period to';
      }
    }
    return '';
  };

  return [
    {
      inputs: {
        closingTypeID: {
          name: 'closingTypeID',
          title: 'Type',
          placeholder: 'Select Closing Type',
          type: FormFieldType.select,
          selectOptions: filteredClosingTypes,
          value: formik.values.closingTypeID,
          required: true
        },
        closingPeriodFrom: {
          name: 'closingPeriodFrom',
          title: 'Closing Period From',
          placeholder: 'Select Closing Period From',
          type: FormFieldType.date,
          value: formik.values.closingPeriodFrom,
          required: true,
          customValidator: dateRangeValidator
        },
        closingPeriodTo: {
          name: 'closingPeriodTo',
          title: 'Closing Period To',
          placeholder: 'Select Closing Period To',
          type: FormFieldType.date,
          value: formik.values.closingPeriodTo,
          required: true,
          minDate: formik.values.closingPeriodFrom
            ? new Date(formik.values.closingPeriodFrom)
            : undefined,
          customValidator: dateRangeValidator
        }
      }
    }
  ];
};
